/**
 * SSO session management against the server-side Federate OIDC flow.
 *
 * This is a pure SSO app: there are no sign-in/sign-out buttons or pages. When
 * the user is not authenticated, the app silently redirects to Federate/Midway
 * (see `redirectToLogin`), and once signed in it keeps the session alive for the
 * full Midway SSO session (~8-12h) with no interruptions.
 *
 * How it works
 * ------------
 * The backend auth Lambda runs the authorization-code + client-secret flow
 * server-side and mints an httpOnly `amzn_sso_token` ID-token cookie plus an
 * httpOnly refresh-token cookie (no token is ever exposed to JS). The ID token
 * still expires in ~1h, so this module keeps it fresh: it probes
 * `/prod/api/auth/login-status` for expiry and renews through a pluggable
 * RenewalStrategy before it lapses.
 *
 * Renewal strategy (the swappable seam)
 * -------------------------------------
 *   - serverRefreshStrategy (default now): a background POST to
 *     `/prod/api/auth/refresh`. The server exchanges the httpOnly refresh-token
 *     cookie for a fresh ID token and rewrites the session cookie — fully silent
 *     and proactive, no page reload, no iframe, no token in the browser.
 *
 *   - redirectStrategy (interactive fallback): when silent refresh fails because
 *     the Midway SSO session has actually ended, navigate to the server login
 *     endpoint (route-preserving), which 302s through Federate/Midway and back.
 */

// Injected at build time by webpack DefinePlugin. Empty string in production
// (app served same-origin behind the CloudFront edge lambda); the API Gateway
// origin in local dev (where there is no Federate interceptor).
declare const API_URL: string;

/**
 * Auth-status probe, served same-origin by the backend auth Lambda. Returns
 * `{ is_authenticated, expires_at, authn_endpoint }`. Replaces the old Mote
 * interceptor's `/sso/login` endpoint.
 */
const SSO_LOGIN_PATH = '/prod/api/auth/login-status';

/**
 * Backend endpoint that silently exchanges the httpOnly refresh-token cookie for
 * a fresh session cookie — no page reload, and no token ever exposed to
 * client-side JS. This is the server-side authorization-code + refresh-token
 * flow that replaced the Mote interceptor, so silent renewal is now the default.
 */
const REFRESH_ENDPOINT = '/prod/api/auth/refresh';

/** With a silent strategy, begin renewing this long before the token's expiry. */
const RENEW_LEAD_MS = 3 * 60 * 1000;
/** Floor for any scheduled timer so we never busy-loop. */
const MIN_DELAY_MS = 5 * 1000;
/** Re-check this long after expiry (so the interceptor has flipped to unauthenticated). */
const POST_EXPIRY_BUFFER_MS = 2 * 1000;
/** Don't re-probe more often than this in response to focus/visibility changes. */
const REPROBE_THROTTLE_MS = 30 * 1000;
/** Guard against reload/redirect loops. */
const HARD_REAUTH_THROTTLE_MS = 15 * 1000;
const HARD_REAUTH_TS_KEY = 'sso_hard_reauth_ts';
/**
 * Cap on automatic sign-in redirects before we stop looping and surface the
 * failure to the user. Covers failures with no explicit `auth_error` signal
 * (e.g. the callback succeeds but cookies don't persist). Counted per tab.
 */
const MAX_SIGNIN_ATTEMPTS = 3;
const SIGNIN_ATTEMPTS_KEY = 'sso_signin_attempts';
/** Max value setTimeout accepts (~24.8 days) before it overflows to 1ms. */
const MAX_TIMER_MS = 0x7fffffff;

interface SsoStatus {
    authenticated: boolean;
    /** Epoch milliseconds at which the current token expires. */
    expiresAt?: number;
    /** Federate authorize URL; present only when unauthenticated. Part of the interceptor contract. */
    authnEndpoint?: string;
}

/** Result of a renewal attempt. */
type RenewOutcome =
    | 'renewed' // a fresh token is now in place; caller may retry requests
    | 'redirected' // a full-page navigation was triggered; execution is ending
    | 'failed'; // could not renew and did not navigate

/**
 * Pluggable renewal mechanism. This is the single seam a future server-side
 * refresh-token flow swaps in behind.
 */
interface RenewalStrategy {
    /** True when renewal happens without navigating away (no page reload). */
    readonly silent: boolean;
    /** Attempt to obtain a fresh session token. */
    renew(status: SsoStatus): Promise<RenewOutcome>;
}

let renewTimer: ReturnType<typeof setTimeout> | null = null;
let refreshInFlight: Promise<boolean> | null = null;
let lastProbeAt = 0;
let started = false;

/**
 * Silent renewal only applies when the app is served through the CloudFront
 * edge lambda (production). Local dev talks to the API Gateway directly and has
 * no Federate interceptor, so this is a no-op there (and under jest).
 */
function isEnabled(): boolean {
    return (
        typeof API_URL === 'string' &&
        API_URL === '' &&
        typeof window !== 'undefined' &&
        typeof document !== 'undefined'
    );
}

/** Query the interceptor for the current auth status. */
async function probe(): Promise<SsoStatus> {
    lastProbeAt = Date.now();
    const res = await fetch(`${window.location.origin}${SSO_LOGIN_PATH}`, {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
    });
    let body: any = {};
    try {
        body = await res.json();
    } catch {
        /* endpoint should always return JSON; treat anything else as unknown */
    }
    return {
        authenticated: body?.is_authenticated === true,
        expiresAt: typeof body?.expires_at === 'number' ? body.expires_at : undefined,
        authnEndpoint: typeof body?.authn_endpoint === 'string' ? body.authn_endpoint : undefined,
    };
}

/** Throttle full-page re-auth so a persistent failure can't loop. Returns true if allowed. */
function allowHardReauth(): boolean {
    try {
        const now = Date.now();
        const last = Number(sessionStorage.getItem(HARD_REAUTH_TS_KEY) || '0');
        if (now - last < HARD_REAUTH_THROTTLE_MS) return false;
        sessionStorage.setItem(HARD_REAUTH_TS_KEY, String(now));
    } catch {
        /* sessionStorage may be unavailable (private mode); proceed anyway */
    }
    return true;
}

// --- Sign-in attempt tracking + failure detection -------------------------

function signInAttempts(): number {
    try {
        return Number(sessionStorage.getItem(SIGNIN_ATTEMPTS_KEY) || '0');
    } catch {
        return 0;
    }
}

function bumpSignInAttempts(): void {
    try {
        sessionStorage.setItem(SIGNIN_ATTEMPTS_KEY, String(signInAttempts() + 1));
    } catch {
        /* sessionStorage may be unavailable; proceed anyway */
    }
}

/** Clear the sign-in attempt counter. Call once a user is successfully loaded. */
export function resetSignInAttempts(): void {
    try {
        sessionStorage.removeItem(SIGNIN_ATTEMPTS_KEY);
    } catch {
        /* ignore */
    }
}

/**
 * The failure reason the server callback reported via `?auth_error=...`
 * (e.g. 'exchange', 'federate', 'nocode'), or null if none. Present when the
 * OIDC callback could not complete sign-in.
 */
export function getAuthError(): string | null {
    if (typeof window === 'undefined') return null;
    try {
        return new URLSearchParams(window.location.search).get('auth_error');
    } catch {
        return null;
    }
}

/**
 * True when sign-in has definitively failed and we must stop auto-redirecting
 * and tell the user: either the server reported an error (`auth_error`), or we
 * have looped through the redirect too many times without establishing a session.
 */
export function signInFailed(): boolean {
    return !!getAuthError() || signInAttempts() >= MAX_SIGNIN_ATTEMPTS;
}

/** Current route to return to after login, with any stale `auth_error` stripped. */
function currentReturnTo(): string {
    try {
        const url = new URL(window.location.href);
        url.searchParams.delete('auth_error');
        return url.pathname + url.search + url.hash;
    } catch {
        return '/';
    }
}

/**
 * Interactive fallback: navigate to the server login endpoint, preserving the
 * current route so the OIDC callback can return the user exactly where they
 * were. The server (auth Lambda) starts the Federate authorization-code flow and,
 * while the Midway SSO session is alive, sends the browser back with fresh
 * httpOnly cookies. (A bare reload would no longer trigger Federate now that the
 * edge interceptor is gone — the redirect must go through the login endpoint.)
 */
const redirectStrategy: RenewalStrategy = {
    silent: false,
    async renew(status: SsoStatus): Promise<RenewOutcome> {
        // Stop looping once sign-in has failed (server error, or too many attempts);
        // the app shell surfaces the failure to the user instead.
        if (signInFailed()) return 'failed';
        if (!allowHardReauth()) return 'failed';
        bumpSignInAttempts();
        const returnTo = currentReturnTo();
        const base = status.authnEndpoint || '/prod/api/auth/login';
        const sep = base.includes('?') ? '&' : '?';
        window.location.assign(`${base}${sep}return_to=${encodeURIComponent(returnTo)}`);
        return 'redirected';
    },
};

/**
 * Silent strategy for a future authorization-code + refresh-token backend. Calls
 * REFRESH_ENDPOINT to mint a fresh cookie without any page navigation; on
 * failure (refresh token invalid/expired) it falls back to interactive re-auth.
 */
const serverRefreshStrategy: RenewalStrategy = {
    silent: true,
    async renew(status: SsoStatus): Promise<RenewOutcome> {
        try {
            const res = await fetch(`${window.location.origin}${REFRESH_ENDPOINT}`, {
                method: 'POST',
                credentials: 'include',
                cache: 'no-store',
                headers: { Accept: 'application/json' },
            });
            if (res.ok) return 'renewed';
        } catch {
            /* fall through to interactive re-auth */
        }
        return redirectStrategy.renew(status);
    },
};

/** Select the active renewal strategy. */
function getStrategy(): RenewalStrategy {
    return REFRESH_ENDPOINT ? serverRefreshStrategy : redirectStrategy;
}

function clearTimer(): void {
    if (renewTimer !== null) {
        clearTimeout(renewTimer);
        renewTimer = null;
    }
}

function scheduleAt(delayMs: number): void {
    clearTimer();
    const delay = Math.min(Math.max(delayMs, MIN_DELAY_MS), MAX_TIMER_MS);
    renewTimer = setTimeout(() => {
        void tick();
    }, delay);
}

/** (Re)schedule the next check based on a known-authenticated status. */
function rescheduleFrom(status: SsoStatus): void {
    if (!status.expiresAt) {
        scheduleAt(RENEW_LEAD_MS);
        return;
    }
    const untilExpiry = status.expiresAt - Date.now();
    if (getStrategy().silent) {
        // Renew before expiry for a zero-gap handoff.
        scheduleAt(untilExpiry - RENEW_LEAD_MS);
    } else {
        // Redirect mode: just re-check once around expiry; renewal itself is
        // deferred to a reactive trigger so we never reload out from under an
        // active user.
        scheduleAt(untilExpiry + POST_EXPIRY_BUFFER_MS);
    }
}

/** Single-flighted renewal via the active strategy. Returns true only if a token was renewed. */
function performRenewal(status: SsoStatus): Promise<boolean> {
    if (refreshInFlight) return refreshInFlight;

    refreshInFlight = (async () => {
        try {
            const outcome = await getStrategy().renew(status);
            if (outcome === 'renewed') {
                const after = await probe();
                if (after.authenticated) {
                    rescheduleFrom(after);
                    return true;
                }
            }
            // 'redirected' → navigating away; 'failed' → retry on next tick/focus.
            return false;
        } catch {
            return false;
        } finally {
            refreshInFlight = null;
        }
    })();

    return refreshInFlight;
}

/** Periodic driver: probe status and either reschedule or renew. */
async function tick(): Promise<void> {
    let status: SsoStatus;
    try {
        status = await probe();
    } catch {
        scheduleAt(REPROBE_THROTTLE_MS);
        return;
    }

    if (!status.authenticated) {
        // Auto-renew proactively only when it can be done silently. In redirect
        // mode we let the token lapse and rely on a reactive trigger (an API call
        // via ensureFreshSession, or the user returning to the tab) so we don't
        // reload the page while someone may be mid-task.
        if (getStrategy().silent) {
            await performRenewal(status);
        }
        return;
    }

    // Authenticated: with a silent strategy, renew a bit before expiry.
    const untilExpiry = status.expiresAt ? status.expiresAt - Date.now() : undefined;
    if (getStrategy().silent && untilExpiry !== undefined && untilExpiry <= RENEW_LEAD_MS) {
        await performRenewal(status);
        return;
    }
    rescheduleFrom(status);
}

/**
 * Start the background session renewal loop. Safe to call once at app startup;
 * a no-op outside production or if already started.
 */
export function startSessionRenewal(): void {
    if (started || !isEnabled()) return;
    started = true;

    void tick();

    // Timers are unreliable across laptop sleep / long background tabs, so also
    // re-check whenever the tab becomes visible or regains focus.
    const onVisible = () => {
        if (document.visibilityState !== 'visible') return;
        if (Date.now() - lastProbeAt < REPROBE_THROTTLE_MS) return;
        void tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
}

/**
 * Ensure the session is fresh right now. Used by the API layer as a safety net:
 * on an auth failure it verifies the session and, if the token has genuinely
 * lapsed, renews it and reports whether the caller can retry. Returns false when
 * the session is still valid (e.g. a genuine permission 403) or when renewal
 * required a navigation, so the caller does not retry needlessly.
 */
export async function ensureFreshSession(): Promise<boolean> {
    if (!isEnabled()) return false;
    const status = await probe();
    // Still authenticated → the failure was not an expired session; don't renew.
    if (status.authenticated) return false;
    return performRenewal(status);
}

/**
 * Begin interactive SSO sign-in, preserving the current route. This is the
 * "pop up the Midway sign-on when the user isn't logged in" entrypoint: a
 * full-page navigation to the server login endpoint, which 302s to
 * Federate/Midway and returns the user to where they were. There is no sign-in
 * button — the app shell calls this automatically when it has no authenticated
 * user.
 *
 * Returns true if a redirect was initiated. Returns false when sign-in can't run
 * here — outside the deployed same-origin app (local dev / tests, no Federate),
 * or when throttled to prevent a redirect loop.
 */
export function redirectToLogin(): boolean {
    if (!isEnabled()) return false;
    // Sign-in has failed (server error or too many attempts) — stop looping so the
    // caller can show the failure notice instead of a perpetual spinner.
    if (signInFailed()) return false;
    // Navigate unless we very recently did (the throttle guards against a redirect
    // loop). Either way we're in the sign-in flow, so report success so the caller
    // keeps showing the "redirecting" state rather than a fallback notice.
    if (allowHardReauth()) {
        bumpSignInAttempts();
        window.location.assign(`/prod/api/auth/login?return_to=${encodeURIComponent(currentReturnTo())}`);
    }
    return true;
}

/**
 * Manual "try again" after a failed sign-in: reset the attempt counter and the
 * redirect throttle, clear the `auth_error` from the URL, and start a fresh
 * sign-in redirect. Bound to the retry button on the failure notice.
 */
export function restartLogin(): void {
    resetSignInAttempts();
    try {
        sessionStorage.removeItem(HARD_REAUTH_TS_KEY);
    } catch {
        /* ignore */
    }
    try {
        const url = new URL(window.location.href);
        url.searchParams.delete('auth_error');
        window.history.replaceState({}, document.title, url.toString());
    } catch {
        /* ignore */
    }
    redirectToLogin();
}
